/**
 * Centralized API client with automatic JWT token attachment and
 * silent token refresh on 401 responses.
 *
 * All authenticated API calls should go through `apiClient.fetch()`.
 */

import { useAuthStore } from '@/stores/authStore'
import { parseExpiry } from '@/components/auth/impersonationText'

/**
 * Error thrown by ApiClient when a request returns a non-2xx response.
 *
 * `status` is the HTTP status code. `detail` is the parsed JSON body
 * of the error response (if any) — typically FastAPI's `{detail: ...}`
 * envelope, where the inner value may be a string, list, or
 * arbitrary object (e.g. structured 422 validation errors).
 */
export class ApiError extends Error {
  status: number
  detail: unknown

  constructor(message: string, status: number, detail: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

async function parseErrorDetail(response: Response): Promise<unknown> {
  try {
    const body = await response.clone().json()
    if (body && typeof body === 'object' && 'detail' in body) {
      return (body as { detail: unknown }).detail
    }
    return body
  } catch {
    return null
  }
}

/** What the response observer is told about each request (SA-1304); never a body or a header. */
export interface ResponseReport {
  method: string
  /** The request path as called, query included; the observer templates it. */
  path: string
  /** The final status, after any refresh retry; 0 when the request never answered. */
  status: number
  ms: number
  /** `detail.code` of a 4xx/5xx JSON body, when present. */
  code?: string
  /** False when the signed-in person changed while the request was in flight. */
  sameIdentity: boolean
}

export type ResponseObserver = (report: ResponseReport) => void

let responseObserver: ResponseObserver | null = null

/** The one observer slot (SA-1304's feedback context); the client imports nothing of it. */
export function setResponseObserver(observer: ResponseObserver | null): void {
  responseObserver = observer
}

async function errorCode(response: Response): Promise<string | undefined> {
  if (response.status < 400) return undefined
  try {
    const body = await response.clone().json()
    const code = body?.detail?.code
    return typeof code === 'string' ? code : undefined
  } catch {
    return undefined
  }
}

class ApiClient {
  /** In-flight refresh promise, used to deduplicate concurrent refresh attempts. */
  private refreshPromise: Promise<string | null> | null = null

  /** Authenticated fetch (see `send`), reported to the response observer when one is set. */
  async fetch(path: string, options: RequestInit = {}): Promise<Response> {
    const observer = responseObserver
    if (!observer) return this.send(path, options)
    const started = Date.now()
    const userAtSend = useAuthStore.getState().user?.id ?? null
    const report = (status: number, code?: string) => {
      try {
        observer({
          method: (options.method ?? 'GET').toUpperCase(),
          path,
          status,
          ms: Date.now() - started,
          code,
          sameIdentity: (useAuthStore.getState().user?.id ?? null) === userAtSend,
        })
      } catch {
        // An observer never breaks a request.
      }
    }
    let response: Response
    try {
      response = await this.send(path, options)
    } catch (error) {
      report(0)
      throw error
    }
    report(response.status, await errorCode(response))
    return response
  }

  /**
   * Authenticated fetch wrapper.
   *
   * - Attaches `Authorization: Bearer <token>` header automatically.
   * - Sends cookies (`credentials: 'include'`) for refresh token.
   * - On 401, attempts a silent token refresh and retries once.
   * - If refresh fails, clears auth state (caller should redirect to login).
   */
  private async send(path: string, options: RequestInit = {}): Promise<Response> {
    const token = useAuthStore.getState().accessToken
    // Decided by how THIS request was sent, not by the store at response time.
    const sentUnderViewAs = useAuthStore.getState().impersonation !== null
    const headers = new Headers(options.headers)

    // Default to JSON content type for requests with a body.
    // Skip for FormData — the browser must set the multipart boundary automatically.
    if (options.body && !headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json')
    }
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const response = await fetch(path, {
      ...options,
      credentials: 'include',
      headers,
    })

    if (response.status === 401) {
      // SA-906 D10: never refresh a request that was sent under a view-as. The
      // cookie is the ADMIN's, so a refresh would silently return the admin
      // wearing the target's name. End the view-as (the banner settles it) and
      // let the caller see the 401. Decided by how the request was SENT: when
      // several in-flight requests 401 together, the first ends the view-as and
      // the rest (stopImpersonation is then a no-op) must still not refresh.
      if (sentUnderViewAs) {
        // A 401 once the stamp has passed is the clock's, not the server's
        // refusal of the user (a tab back from sleep): word it as an expiry.
        const imp = useAuthStore.getState().impersonation
        const expired = imp !== null && !(parseExpiry(imp.expiresAt) > Date.now())
        useAuthStore.getState().stopImpersonation(expired ? 'expired' : 'unauthorized')
        return response
      }

      // Attempt silent refresh (deduplicated)
      const newToken = await this.refreshToken()
      if (newToken) {
        // Retry the original request with the new token
        const retryHeaders = new Headers(options.headers)
        if (options.body && !retryHeaders.has('Content-Type') && !(options.body instanceof FormData)) {
          retryHeaders.set('Content-Type', 'application/json')
        }
        retryHeaders.set('Authorization', `Bearer ${newToken}`)

        return fetch(path, {
          ...options,
          credentials: 'include',
          headers: retryHeaders,
        })
      }

      // Refresh failed — clear auth state
      useAuthStore.getState().clearAuth()
    }

    return response
  }

  /**
   * Attempt to refresh the access token using the httpOnly refresh cookie.
   * Returns the new access token, or null on failure.
   * Deduplicates concurrent calls so only one refresh request is in-flight.
   */
  private async refreshToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this._doRefresh()
    try {
      return await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  private async _doRefresh(): Promise<string | null> {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        const store = useAuthStore.getState()
        // SA-906: a refresh is always the ADMIN's (the cookie is theirs). One
        // that lands while viewing as — a request sent before Start that 401s
        // after it — replaces the KEPT token, never the one in use, and its
        // flags are the admin's too: the target's banner stays the target's.
        if (store.impersonation) {
          store.setActorToken(data.access_token)
          return data.access_token
        }
        store.setAccessToken(data.access_token)
        if (data.must_change_password !== undefined) {
          store.setMustChangePassword(data.must_change_password)
        }
        if (data.must_accept_disclaimer !== undefined) {
          store.setMustAcceptDisclaimer(data.must_accept_disclaimer)
        }
        return data.access_token
      }
    } catch {
      // Network error — refresh failed
    }
    return null
  }
  /** Convenience: GET request, returns parsed JSON (throws on non-OK). */
  async get<T = any>(path: string): Promise<T> {
    const response = await this.fetch(path)
    if (!response.ok) {
      const detail = await parseErrorDetail(response)
      throw new ApiError(
        `GET ${path} failed: ${response.statusText}`,
        response.status,
        detail,
      )
    }
    return response.json()
  }

  /** Convenience: POST request with JSON body, returns parsed JSON (throws on non-OK). */
  async post<T = any>(path: string, body: unknown): Promise<T> {
    const response = await this.fetch(path, {
      method: 'POST',
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const detail = await parseErrorDetail(response)
      throw new ApiError(
        `POST ${path} failed: ${response.statusText}`,
        response.status,
        detail,
      )
    }
    return response.json()
  }

  /** Convenience: PUT request with JSON body, returns parsed JSON (throws on non-OK). */
  async put<T = any>(path: string, body: unknown): Promise<T> {
    const response = await this.fetch(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const detail = await parseErrorDetail(response)
      throw new ApiError(
        `PUT ${path} failed: ${response.statusText}`,
        response.status,
        detail,
      )
    }
    return response.json()
  }

  /** Convenience: DELETE request (throws on non-OK). */
  async delete(path: string): Promise<void> {
    const response = await this.fetch(path, { method: 'DELETE' })
    if (!response.ok) {
      const detail = await parseErrorDetail(response)
      throw new ApiError(
        `DELETE ${path} failed: ${response.statusText}`,
        response.status,
        detail,
      )
    }
  }
}

export const apiClient = new ApiClient()
